import * as api from 'helpers/api'
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import * as authHelper from 'helpers/authHelper'
import { getLogin, getLoginWithNonce, getLogout } from './loginSlice'
import type { SavedBucketDto } from 'store/types'

// ------------------------------------------------------------
export const getInit = createAsyncThunk(
  'getInit',
  async (params, { rejectWithValue, dispatch }) => {
    try {
      const data = await api.get('/me')

      if (!data) {
        return rejectWithValue([])
      }
      return data
    } catch (error) {
      console.log('error', error)
      const { response, message } = (error ?? {}) as {
        response?: { status?: number; data?: unknown }
        message?: string
      }
      if (response?.status === 401) {
        await authHelper.logout()
        dispatch(getLogout())
      }
      // Always settle as rejected. Swallowing the error resolved the thunk as
      // *fulfilled* with no payload, so `rejected` stayed false while `userType`
      // stayed empty — and the Login screen's effect re-dispatched getInit on
      // every resulting state change, hammering /me in a tight loop whenever the
      // API was unreachable.
      return rejectWithValue(response?.data ?? message ?? null)
    }
  },
)

// ------------------------------------------------------------

type Office = {
  id: number
  name: string
}

const initialState = {
  pending: false,
  id: 0,
  userType: '',
  adminOf: null,
  isSsoEnabled: false,
  isSuperadmin: false,
  company: {
    name: '',
    id: '',
  },
  primaryOfficeId: '',
  currentOfficeId: '',
  physicianGroup: '',
  offices: [{ id: 0, officeName: 'office' }],
  currentOfficeTests: {},
  roles: [] as string[],
  permissions: [] as string[],
  notificationBuckets: [] as string[],
  savedBuckets: [] as SavedBucketDto[],
  title: '',
  profile: {
    firstName: '',
    lastName: '',
    email: '',
  },
  unreadBulletins: {},
  rejected: false,
}

// ------------------------------------------------------------
const localSlice = createSlice({
  name: 'init',
  initialState,
  reducers: {
    setCurrentOfficeTests: (state, action) => {
      state.currentOfficeTests = action.payload
    },
    setSavedBuckets: (state, action) => {
      state.savedBuckets = action.payload ?? []
    },
    setNotificationBuckets: (state, action) => {
      state.notificationBuckets = action.payload ?? []
    },
    switchOffice: (state, action) => {
      state.currentOfficeId = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getInit.pending, (state, action) => {
      state.id = 0
      state.userType = ''
      state.company = {
        name: '',
        id: '',
      }
      state.primaryOfficeId = ''
      state.currentOfficeId = ''
      state.physicianGroup = ''
      state.currentOfficeTests = {}
      state.offices = []
      state.roles = []
      state.permissions = []
      state.notificationBuckets = []
      state.savedBuckets = []
      state.title = ''
      state.isSuperadmin = false
      state.profile = {
        firstName: '',
        lastName: '',
        email: '',
      }
      state.pending = true
      state.rejected = false
    })
    builder.addCase(getInit.fulfilled, (state, action) => {
      state.pending = false
      state.rejected = false
      if (!action?.payload) return
      state.id = action.payload?.id
      state.company.name = action.payload.company?.companyName
      state.company.id = action.payload.company?.id
      state.profile.firstName = action.payload.firstName
      state.profile.lastName = action.payload.lastName
      state.profile.email = action.payload.email
      state.offices = action.payload.offices
      state.roles = action.payload.roles
      state.permissions = action.payload.permissions ?? []
      state.notificationBuckets = action.payload.notificationBuckets ?? []
      state.savedBuckets = action.payload.savedBuckets ?? []
      state.title = action.payload.title
      state.physicianGroup = action.payload.physicianGroup
      state.primaryOfficeId = action.payload.primaryOffice
      state.currentOfficeId = action.payload.primaryOffice
      state.userType = action.payload.userType
      state.adminOf = action.payload.adminOf
      state.isSuperadmin = action.payload.isSuperadmin ?? false
      state.unreadBulletins = action.payload.unreadBulletins
    })
    builder.addCase(getInit.rejected, (state, action) => {
      state.id = 0
      state.userType = ''
      state.company.name = ''
      state.company.id = ''
      state.primaryOfficeId = ''
      state.currentOfficeId = ''
      state.physicianGroup = ''
      state.offices = []
      state.roles = []
      state.permissions = []
      state.notificationBuckets = []
      state.savedBuckets = []
      state.title = ''
      state.isSuperadmin = false
      state.profile = {
        firstName: '',
        lastName: '',
        email: '',
      }
      state.pending = false
      state.rejected = true
    })
    // A successful sign-in clears a previously failed init so the Login screen is
    // allowed to fetch /me again (see the `rejected` guard there).
    builder.addCase(getLogin.fulfilled, (state) => {
      state.rejected = false
    })
    builder.addCase(getLoginWithNonce.fulfilled, (state) => {
      state.rejected = false
    })
    builder.addCase(getLogout.fulfilled, () => initialState)
  },
})

export const { setCurrentOfficeTests, setSavedBuckets, setNotificationBuckets, switchOffice } = localSlice.actions

export default localSlice.reducer
